import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';

import { CompareToggle } from '@shared-ui/retail-compare-toggle';
import { LocalizedComponentProps, withLocalization } from '@shared-ui/localization-context';
import { SortAndFiltersViewType } from '@shared-ui/retail-sort-and-filter-lodging';
import { TOGGLE_PLACEMENT_SRP } from '@shared-ui/retail-compare-providers';
import { useClickstreamAnalytics } from '@shared-ui/clickstream-analytics-context';
import { ViewExtraLarge, ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { evaluateAndLogExperiment } from 'stores/utils';
import { SearchResultsStore } from 'stores/search-results-store';
import { useViewTrackerContext } from 'src/common/stores/contexts/viewTracker';

import { SearchFormFields } from 'src/common/models';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';

import { MapPreview } from 'src/common/components/search/map/map-preview';
import { ResultHeaderFilterPills } from './result-header-filter-pills';
import { ResultsSubHeader } from './results-sub-header';
import { UniversalSortFilterWrapper } from './sort-filter/universal-sort-filter';

export interface ResultsHeaderProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
  withMapButton?: boolean;
  withFilterButton?: boolean;
  experiment: ExperimentStore;
  context?: ContextStore;
  page?: PageStore;
}

export interface ResultsHeaderState {
  resultsHeaderActive: boolean;
}

// @ts-ignore
@withLocalization
@inject('context', 'searchResults', 'experiment', 'page')
@observer
export class ResultsHeader extends React.Component<ResultsHeaderProps, ResultsHeaderState> {
  private searchResultsHeaderWrapper: any;

  public static defaultProps = {
    l10n: null,
    withMapButton: false,
    withFilterButton: false,
  };

  constructor(props: ResultsHeaderProps) {
    super(props);

    this.state = { resultsHeaderActive: false };
    this.searchResultsHeaderWrapper = React.createRef();
  }

  public componentDidMount() {
    this.setState({
      resultsHeaderActive: true,
    });
  }

  public render() {
    const { l10n, searchResults, withMapButton, withFilterButton, experiment, context, page } = this.props;

    if (!searchResults) {
      return null;
    }

    const { isLoading, loyaltyMessagingModel, searchCriteria, sortAndFilter, resultMessages } = searchResults;
    const isQuickAccessFiltersActive =
      evaluateAndLogExperiment('Introduce_Quick_Sort_and_Filters_at_top_of_SRP_web', context, experiment, page) === 1 ||
      evaluateAndLogExperiment('Introduce_Quick_Sort_and_Filters_at_top_of_SRP_mweb', context, experiment, page) === 1;
    const pills = sortAndFilter && sortAndFilter.pills;
    const filters = searchResults && searchResults.searchAndFilterDisplayOptions && searchResults.searchAndFilterDisplayOptions.filters;
    const ResultsSubHeaderProps = {
      resultMessages,
      isLoading,
      l10n,
      loyaltyMessagingModel,
      searchCriteria,
      isQuickAccessFiltersActive,
    };
    const buttonsProps = {
      isActive: this.state.resultsHeaderActive,
      filters,
      dialogTitle: l10n.formatText('hotels.searchResults.filtersDialog.title'),
      withMapButton,
      withFilterButton,
    };

    const shouldRenderPills = pills && pills.length > 0 && !isQuickAccessFiltersActive;

    return (
      <ErrorBoundary label="hotel.search.tools">
        <UitkSpacing margin={{ blockstart: 'one' }}>
          <section data-stid="results-header" ref={this.searchResultsHeaderWrapper.value}>
            {!isQuickAccessFiltersActive && <ButtonsParent {...(buttonsProps as any)} experiment={experiment} />}
            <CompareToggle placement={TOGGLE_PLACEMENT_SRP} />
            {shouldRenderPills && <ResultHeaderFilterPills />}
            <ResultsSubHeader {...ResultsSubHeaderProps} />
          </section>
        </UitkSpacing>
      </ErrorBoundary>
    );
  }
}

const Buttons = inject(
  'context',
  'page'
)(({ withFilterButton, withMapButton, experiment, context, page }: ButtonsProps) => {
  const viewTracker = useViewTrackerContext();
  const tracker = useClickstreamAnalytics();

  const logPriceSliderExposureCallback = () => {
    evaluateAndLogExperiment('Improve_Price_Slider_on_mweb', context, experiment, page);
    evaluateAndLogExperiment('Filter_Display_Migration_for_BEX_and_HOB_Mweb', context, experiment, page);
  };

  return (
    <UitkSpacing data-stid="tools__actions">
      <UitkLayoutFlex>
        {withFilterButton && (
          <UitkLayoutFlexItem grow={2} data-stid="filters-toggle-button">
            <UitkSpacing padding={{ blockend: 'three' }}>
              <div>
                <UitkLayoutFlex>
                  <UitkLayoutFlexItem grow={2}>
                    <UniversalSortFilterWrapper
                      view={SortAndFiltersViewType.SORT_AND_FILTERS}
                      onOpenDialog={logPriceSliderExposureCallback}
                    />
                  </UitkLayoutFlexItem>
                </UitkLayoutFlex>
              </div>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        )}
        {withMapButton && (
          <UitkLayoutFlexItem grow={1} data-stid="map-preview-button">
            <UitkSpacing margin={{ inline: 'one' }}>
              <div>
                <MapPreview viewTracker={viewTracker} tracker={tracker} />
              </div>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        )}
      </UitkLayoutFlex>
    </UitkSpacing>
  );
});

interface ButtonsProps {
  isActive: boolean;
  filters: SearchFormFields;
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
  classes?: string;
  experiment: ExperimentStore;
  context?: ContextStore;
  page?: PageStore;
}

const ButtonsParent = ({ isActive, filters, dialogTitle, withFilterButton, withMapButton, experiment }: ButtonsParentProps) => (
  <Viewport>
    <ViewSmall>
      <Buttons
        isActive={isActive}
        filters={filters}
        dialogTitle={dialogTitle}
        withFilterButton={true}
        withMapButton={true}
        experiment={experiment}
      />
    </ViewSmall>
    <ViewLarge>
      <></>
    </ViewLarge>
    <ViewExtraLarge>
      <Buttons
        isActive={isActive}
        filters={filters}
        dialogTitle={dialogTitle}
        withMapButton={withMapButton}
        withFilterButton={withFilterButton}
        experiment={experiment}
      />
    </ViewExtraLarge>
  </Viewport>
);

interface ButtonsParentProps {
  isActive: boolean;
  filters: SearchFormFields;
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
  experiment: ExperimentStore;
  context: ContextStore;
  page: PageStore;
}
